import { useCallback, useRef, useState } from "react";
import {
  accountService,
  subscriptionService,
  taskService,
} from "@miplatform/api";
import { HTTP_STATUSES, PRINT_ORIENTATION } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  buildActivityDateFilter,
  createUUID,
  simpleSort,
} from "@miplatform/utils";
import { usePermissions, usePrint, useSession } from "@mibuildweb/hooks";
import { useHomeDetailsScreenViewModel } from "../../../HomeDetails/useHomeDetailsScreenViewModel";
import { attachEventTime, filterIssuesWithHistoryOnAttempt } from "../../utils";
import {
  ASSIGNMENTS_PRINT_HEADER_FIELDS,
  ASSIGNMENTS_PRINT_SECTIONS,
  ASSIGNMENTS_PRINT_STATUS_FIELD,
} from "./constants";

const getUsersUUID = createUUID();
const getAttemptUUID = createUUID();

export const useCloseoutDrawerModel = ({
  attemptId,
  orderId,
  onAttemptUpdated = () => {},
}) => {
  const {
    session: { tenantId },
  } = useSession();

  const { hasPermission } = usePermissions();
  const printDataRef = useRef({});
  const [attempt, setAttempt] = useState({});
  const [nextAttempt, setNextAttempt] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [orderTemplate, setOrderTemplate] = useState(null);
  const [attemptUser, setAttemptUser] = useState(null);
  const [attemptActivities, setAttemptActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [order, setOrder] = useState(null);

  // TODO replace with useHome hook
  const {
    home: attemptHome,
    documents: homeDocuments,
    getHome,
    isLoading,
  } = useHomeDetailsScreenViewModel();

  const { handlePrintSectionsPages } = usePrint(tenant);

  const stateManager = useComponentState();

  const getTenantAccount = () => {
    stateManager.run(async () => {
      const response = await subscriptionService.subscriptions.get(tenantId);

      if (response.status === HTTP_STATUSES.OK) {
        setTenant(response.data);
      }
    });
  };

  const getMappedAttempt = (attempt) => {
    const issues = filterIssuesWithHistoryOnAttempt(attempt).map((issue) => {
      issue.invoiceItems = issue.invoiceItems?.sort((a, b) =>
        simpleSort(a.createdOn, b.createdOn)
      );
      return issue;
    });
    return {
      ...attachEventTime(attempt),
      issues,
      unfilteredIssues: attempt.issues,
    };
  };

  const getAttempt = useCallback(
    (shouldUpdateAttempt) => {
      stateManager.abort(getAttemptUUID);

      return stateManager.run(async () => {
        const { status, data } = await taskService.attempts.get(attemptId);

        if (status === HTTP_STATUSES.OK) {
          setAttempt(getMappedAttempt(data));
          if (shouldUpdateAttempt) {
            onAttemptUpdated(data);
          }
          return status;
        }
        return null;
      }, getAttemptUUID);
    },
    [attemptId]
  );

  const getOrderTemplate = async (id) => {
    stateManager.run(async () => {
      const { status, data } = await taskService.orderTemplates.getById({ id });

      if (status === HTTP_STATUSES.OK) {
        setOrderTemplate(data);
      }
    });
  };

  const getOrder = async () => {
    if (!orderId) {
      return;
    }
    stateManager.run(async () => {
      const { status, data } = await taskService.order.getById({ orderId });

      if (status === HTTP_STATUSES.OK) {
        setOrder(data);
      }
    });
  };

  const closeoutAttempt = () => {
    stateManager.run(async () => {
      const { data: updatedAttempt, status } =
        await taskService.attempts.closeout({
          data: { attemptId },
          modifiedOn: attempt.modifiedOn,
          toastMessageConfig: {
            success: {
              text: `Attempt closed with ${
                attempt.pendingTasksCount || 0
              } incomplete task(s)${
                attempt.pendingIssuesCount > 0
                  ? ` and ${attempt.pendingIssuesCount} issue(s)`
                  : ""
              }.`,
            },
          },
        });

      if (status === HTTP_STATUSES.OK) {
        onAttemptUpdated(updatedAttempt);
        getNextAttempt(updatedAttempt.assignment.id);
      }
    });
  };

  const updateVerificationStatus = async ({ action, successMessage }) => {
    const response = await stateManager.run(() =>
      action({
        data: { attemptId },
        toastMessageConfig: {
          success: { text: successMessage },
        },
      })
    );

    if (response?.status !== HTTP_STATUSES.OK) {
      return false;
    }

    setAttempt(getMappedAttempt(response.data));
    onAttemptUpdated(response.data);
    return true;
  };

  const requestVerification = () =>
    updateVerificationStatus({
      action: taskService.attempts.requestVerification,
      successMessage: "Verification review requested.",
    });

  const completeVerification = () =>
    updateVerificationStatus({
      action: taskService.attempts.verify,
      successMessage: "Verification review completed.",
    });

  const inProgressAttempt = () => {
    stateManager.run(async () => {
      const { data, status } = await taskService.attempts.closeoutInProgress({
        data: { attemptId: attempt.id },
        modifiedOn: attempt.modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Attempt Closeout Started.",
            subtext: `${attempt.home.address?.addressLine1} has started closeout.`,
          },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        onAttemptUpdated(data);
      }
    });
  };

  const getAttemptActivities = useCallback(
    (
      dateRange = {
        startDate: null,
        endDate: null,
      }
    ) => {
      stateManager.run(async () => {
        const response = await taskService.activities.getAll({
          params: {
            refId: `task-service/orders/${attempt.orderId}/assignments/${attempt.assignment?.id}/attempts/${attemptId}`,
          },
          filters: buildActivityDateFilter(dateRange),
        });

        if (response.status === HTTP_STATUSES.OK) {
          setAttemptActivities(
            response.data.sort(
              (a, b) => new Date(b.createdOn) - new Date(a.createdOn)
            )
          );
        }
      });
    },
    [attempt.orderId]
  );

  const getAccount = () => {
    if (!attempt?.orderAccountId) {
      printDataRef.current.account = { name: "--" };
      return Promise.resolve();
    }
    return stateManager.run(async () => {
      const response = await accountService.accounts.getAccountById({
        id: attempt?.orderAccountId,
      });
      if (response.status === HTTP_STATUSES.OK) {
        printDataRef.current.account = response.data;
      }
    });
  };

  const getUser = (id) =>
    stateManager.run(async () => {
      const response = await subscriptionService.users.get(id);
      if (response.status === HTTP_STATUSES.OK) {
        setAttemptUser(response.data);
      }
    });

  const handlePrint = () => {
    getAccount().then(() => {
      if (!printDataRef.current.account || !order) {
        return;
      }
      handlePrintSectionsPages(
        {
          ...printDataRef.current,
          order,
          attempt: {
            ...attempt,
            issues: attempt.issues.map((issue, i) => ({
              number: i + 1,
              ...issue,
            })),
          },
        },
        ASSIGNMENTS_PRINT_SECTIONS,
        {
          headerFields: ASSIGNMENTS_PRINT_HEADER_FIELDS,
          statusHeaderField: ASSIGNMENTS_PRINT_STATUS_FIELD,
          orientation: PRINT_ORIENTATION.PORTRAIT,
        }
      );
    });
  };

  const getNextAttempt = (assignmentId) => {
    stateManager.run(async () => {
      const { data, status } = await taskService.attempts.getLatestAttempt(
        assignmentId
      );

      if (status === HTTP_STATUSES.OK) {
        setNextAttempt({
          ...data,
          issues: filterIssuesWithHistoryOnAttempt(data),
          unfilteredIssues: data.issues,
        });
      }
    });
  };

  /**
   * Get the list of user IDs that handled issues in the attempt, previous attempts, and multi-technician attempts
   *
   * @param {Object} attempt
   * @returns {List<string>} - List of unique user IDs associated with issues in the attempt
   */
  const getIssueUserIds = (a) => {
    const collectIdsFromIssues = (issues = []) =>
      issues.flatMap((issue) => [
        issue.submittedByUserId,
        issue.issueResult?.submittedByUserId,
      ]);

    const ids = [
      ...collectIdsFromIssues(a.issues),
      a.assignedUserId,
      ...collectIdsFromIssues(a.previousAttempt?.issues),
      a.previousAttempt?.assignedUserId,
      ...collectIdsFromIssues(a.multiTechnicianAttempt?.issues),
      a.multiTechnicianAttempt?.assignedUserId,
    ];

    return [...new Set(ids.filter(Boolean))];
  };

  /**
   * Retrieve related users for the given attempt
   */
  const getRelatedUsers = (attempt) => {
    const userIds = getIssueUserIds(attempt);

    stateManager.abort(getUsersUUID);
    stateManager.run(async () => {
      const response = await subscriptionService.users.getAll({
        filters: { id: { in: userIds } },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setUsers(response.data);
      } else {
        setUsers([]);
      }
    }, getUsersUUID);
  };

  const updateOrder = ({ orderId, data, orderModifiedOn }) => {
    stateManager.run(async () => {
      const res = await taskService.order.update({
        orderId,
        data: {
          requestedStartDate: data.orderRequestDate,
          timePreference: data.orderTimePreference,
          priority: data.orderPriority,
          notes: data.orderNotes,
          modifiedOn: orderModifiedOn,
        },
      });

      if (res.status === HTTP_STATUSES.OK) {
        getAttempt(true);
      }
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    attempt,
    attemptHome,
    attemptUser,
    attemptActivities,
    documents: [...homeDocuments, ...(order?.documents || [])],
    orderTemplate,
    loading: isLoading || stateManager.isBusy(),
    nextAttempt,
    order,
    getAttempt,
    getOrder,
    getTenantAccount,
    tenant,
    getHome,
    getOrderTemplate,
    getUser,
    getAttemptActivities,
    closeoutAttempt,
    requestVerification,
    completeVerification,
    inProgressAttempt,
    handlePrint,
    setAttempt,
    stateManager,
    hasPermission,
    getRelatedUsers,
    users,
    updateOrder,
  };
};
